import apiClient from './api';

export interface SepaCreateRequest {
  iban?: string;
}

export interface DokumentLink {
  link: string;
}

export interface DokumentStatusResponse {
  zulassungsantragId: number;
  vollmachtExists: boolean;
  vollmachtLink: string | null;
  sepaExists: boolean;
  sepaLink: string | null;
}

export class DokumentService {
  private static readonly BASE_URL = '/zulassungsantraege';

  // Vollmacht Management
  static async getVollmachtLink(zulassungsantragId: number): Promise<DokumentLink> {
    const response = await apiClient.get(`${this.BASE_URL}/${zulassungsantragId}/vollmacht`);
    return response.data;
  }

  static async createVollmacht(zulassungsantragId: number): Promise<DokumentLink> {
    const response = await apiClient.post(`${this.BASE_URL}/${zulassungsantragId}/vollmacht`);
    return response.data;
  }

  static async deleteVollmacht(zulassungsantragId: number): Promise<void> {
    await apiClient.delete(`${this.BASE_URL}/${zulassungsantragId}/vollmacht`);
  }

  static async downloadVollmacht(zulassungsantragId: number): Promise<Blob> {
    const response = await apiClient.get(`${this.BASE_URL}/${zulassungsantragId}/vollmacht/download`, {
      responseType: 'blob'
    });
    return response.data;
  }

  static async checkVollmachtExists(zulassungsantragId: number): Promise<boolean> {
    try {
      await this.getVollmachtLink(zulassungsantragId);
      return true;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  // SEPA Management
  static async getSepaLink(zulassungsantragId: number): Promise<DokumentLink> {
    const response = await apiClient.get(`${this.BASE_URL}/${zulassungsantragId}/sepa`);
    return response.data;
  }

  static async createSepaMandat(zulassungsantragId: number, request?: SepaCreateRequest): Promise<DokumentLink> {
    const response = await apiClient.post(`${this.BASE_URL}/${zulassungsantragId}/sepa`, request || {});
    return response.data;
  }

  static async deleteSepaMandat(zulassungsantragId: number): Promise<void> {
    await apiClient.delete(`${this.BASE_URL}/${zulassungsantragId}/sepa`);
  }

  static async downloadSepa(zulassungsantragId: number): Promise<Blob> {
    const response = await apiClient.get(`${this.BASE_URL}/${zulassungsantragId}/sepa/download`, {
      responseType: 'blob'
    });
    return response.data;
  }

  static async checkSepaExists(zulassungsantragId: number): Promise<boolean> {
    try {
      await this.getSepaLink(zulassungsantragId);
      return true;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  // Combined Document Status - single request, no 404 errors
  static async getDokumenteStatus(zulassungsantragId: number): Promise<DokumentStatusResponse> {
    const response = await apiClient.get(`${this.BASE_URL}/${zulassungsantragId}/dokumente/status`);
    return response.data;
  }
}
